import React, { useState, useEffect, useMemo, FormEvent } from 'react';
import {
  toUTCNoonDate,
  formatDateForInput,
  calculateNextDueDate,

} from '../../utils/dateUtils';
import {
  TextField,
  MenuItem,
  Button,
  Stack,
  InputLabel,
  Select,
  FormControl,
  FormControlLabel,
  Snackbar,
  Alert,
  Checkbox,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { Transaction } from '../../types/Transaction';
import { useAccounts } from '../../hooks/useAccounts';
import ManageAssetAccountsModal from '../dashboard/ManageAssetAccountsModal';
import { authFetch } from '../../lib/authFetch';

type LegacyType = 'income' | 'expense' | 'savings' | '';
type LedgerKind = 'income' | 'expense' | 'transfer' | 'cardPurchase' | 'cardPayment' | 'adjustment' | '';
type AdjustmentDirection = 'increase' | 'decrease';

interface TransactionFormProps {
  onSuccess: () => void;
  transaction?: Transaction | null;
  defaultDate?: string;
  submitOverride?: (payload: Record<string, unknown>) => Promise<void>;
  disableDateEdit?: boolean;
  disablePaymentEdit?: boolean;
}

const derivePaymentStatus = (tx?: Transaction | null): 'unpaid' | 'paid' =>
  tx?.paymentStatus === 'paid' || Boolean(tx?.paidAt) ? 'paid' : 'unpaid';

const TransactionForm: React.FC<TransactionFormProps> = ({
  onSuccess,
  transaction,
  defaultDate,
  submitOverride,
  disableDateEdit = false,
  disablePaymentEdit = false,
}) => {
  const { auth } = useAuth();
  const {
    accounts,
    loading: accountsLoading,
    checkingAccounts,
    savingsAccounts,
    creditCardAccounts,
    assetAccounts,
    refetch,
  } = useAccounts();

  const isEditingLegacy = !!transaction && transaction.source !== 'ledger' && !transaction.kind;

  const [amount, setAmount] = useState<number | ''>(transaction?.amount || '');
  const [type, setType] = useState<LegacyType>(transaction?.type || '');
  const [kind, setKind] = useState<LedgerKind>(transaction?.kind || '');
  const [category, setCategory] = useState(transaction?.category || '');
  const [description, setDescription] = useState(transaction?.description || '');
  const [date, setDate] = useState(
    transaction?.date ? formatDateForInput(transaction.date) : defaultDate || ''
  );
  const [isRecurring, setIsRecurring] = useState<boolean>(transaction?.isRecurring || false);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | ''>(
    transaction?.frequency || ''
  );
  const [endDate, setEndDate] = useState(transaction?.endDate ? formatDateForInput(transaction.endDate) : '');
  const [tags, setTags] = useState<string[]>(transaction?.tags || []);
  const tagOptions = ['bill', 'utility', 'paycheck', 'bonus', 'subscription', 'groceries', 'education', 'other'];
  const [tentative, setTentative] = useState<boolean>(transaction?.tentative || false);
  const [ownerScope, setOwnerScope] = useState<'primary' | 'partner' | 'child' | 'household'>(
    transaction?.ownerScope || 'household'
  );
  const [visibility, setVisibility] = useState<'shared' | 'private'>(
    transaction?.visibility || 'shared'
  );
  const [nextDueDate, setNextDueDate] = useState(() => {
    const base = transaction?.nextDueDate || transaction?.date;
    return base ? formatDateForInput(base) : '';
  });

  const [accountId, setAccountId] = useState(transaction?.accountId || '');
  const [fromAccountId, setFromAccountId] = useState(transaction?.fromAccountId || '');
  const [toAccountId, setToAccountId] = useState(transaction?.toAccountId || '');
  const [adjustmentDirection, setAdjustmentDirection] = useState<AdjustmentDirection>(
    transaction?.amountSigned !== undefined && transaction.amountSigned < 0 ? 'decrease' : 'increase'
  );
  const [paymentStatus, setPaymentStatus] = useState<'unpaid' | 'paid'>(() => derivePaymentStatus(transaction));

  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [manageCheckingOpen, setManageCheckingOpen] = useState(false);
  const [manageSavingsOpen, setManageSavingsOpen] = useState(false);

  const isLedgerMode = transaction?.source === 'ledger' || !!transaction?.kind || !isEditingLegacy;
  const supportsLedgerRecurringKind =
    kind === 'income' ||
    kind === 'expense' ||
    kind === 'transfer' ||
    kind === 'cardPurchase' ||
    kind === 'cardPayment';
  const showRecurringBlock = isEditingLegacy || (isLedgerMode && supportsLedgerRecurringKind);

  const activeCheckingAccounts = useMemo(
    () => checkingAccounts.filter((account) => !account.isArchived),
    [checkingAccounts]
  );
  const activeSavingsAccounts = useMemo(
    () => savingsAccounts.filter((account) => !account.isArchived),
    [savingsAccounts]
  );
  const selectableAssetAccounts = useMemo(
    () => assetAccounts.filter((account) => !account.isArchived),
    [assetAccounts]
  );
  const defaultCheckingId = activeCheckingAccounts[0]?._id || '';
  const defaultSavingsId = activeSavingsAccounts[0]?._id || '';
  const showMissingBankAccountsAlert =
    isLedgerMode &&
    !accountsLoading &&
    activeCheckingAccounts.length === 0 &&
    activeSavingsAccounts.length === 0;
  const eligibleCreditCardAccounts = useMemo(
    () => creditCardAccounts.filter((account) => account.accountCategory === 'liability'),
    [creditCardAccounts]
  );
  const hasEligibleCreditCardAccounts = eligibleCreditCardAccounts.length > 0;
  const selectedCardPurchaseAccountMissing =
    isLedgerMode &&
    kind === 'cardPurchase' &&
    !!accountId &&
    !eligibleCreditCardAccounts.some((account) => account._id === accountId);
  const selectedCardPaymentCreditCardMissing =
    isLedgerMode &&
    kind === 'cardPayment' &&
    !!toAccountId &&
    !eligibleCreditCardAccounts.some((account) => account._id === toAccountId);
  const disableSubmit =
    accountsLoading ||
    showMissingBankAccountsAlert ||
    (isLedgerMode && (kind === 'cardPurchase' || kind === 'cardPayment') && !hasEligibleCreditCardAccounts);

  useEffect(() => {
    if (!isLedgerMode || !accounts.length) return;

    setKind((prev) => prev || 'expense');

    if ((kind === 'income' || kind === 'expense' || kind === 'adjustment') && !accountId && defaultCheckingId) {
      setAccountId(defaultCheckingId);
    }
    if (kind === 'transfer') {
      if (!fromAccountId && defaultCheckingId) setFromAccountId(defaultCheckingId);
      if (!toAccountId && defaultSavingsId) setToAccountId(defaultSavingsId);
    }
  }, [
    isLedgerMode,
    accounts,
    kind,
    accountId,
    fromAccountId,
    toAccountId,
    defaultCheckingId,
    defaultSavingsId,
  ]);

  // ⏳ Auto-set nextDueDate if it's a new transaction and date + frequency change
  useEffect(() => {
    if (!transaction && isRecurring && date && frequency) {
      const calculated = calculateNextDueDate(date, frequency);
      if (calculated) setNextDueDate(calculated);
    }
  }, [isRecurring, date, frequency, transaction]);

  useEffect(() => {
    if (isLedgerMode && kind === 'adjustment' && isRecurring) {
      setIsRecurring(false);
      setFrequency('');
      setNextDueDate('');
      setEndDate('');
    }
  }, [isLedgerMode, kind, isRecurring]);

  const txPaymentStatus = transaction?.paymentStatus;
  const txPaidAt = transaction?.paidAt;
  useEffect(() => {
    setPaymentStatus(txPaymentStatus === 'paid' || Boolean(txPaidAt) ? 'paid' : 'unpaid');
  }, [transaction?._id, txPaymentStatus, txPaidAt]);

  const validateLedgerAccounts = (): boolean => {
    if (kind === 'income' || kind === 'expense') {
      return !!accountId;
    }
    if (kind === 'transfer') {
      return !!fromAccountId && !!toAccountId && fromAccountId !== toAccountId;
    }
    if (kind === 'cardPurchase') {
      return (
        !!accountId &&
        eligibleCreditCardAccounts.some((account) => account._id === accountId)
      );
    }
    if (kind === 'cardPayment') {
      return (
        !!fromAccountId &&
        !!toAccountId &&
        fromAccountId !== toAccountId &&
        selectableAssetAccounts.some((account) => account._id === fromAccountId) &&
        eligibleCreditCardAccounts.some((account) => account._id === toAccountId)
      );
    }
    if (kind === 'adjustment') {
      return !!accountId;
    }
    return false;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isLedgerMode) {
      if (!amount || !kind || !category || !date) {
        setError('All fields except description are required.');
        setSnackbarOpen(true);
        return;
      }

      if ((kind === 'cardPurchase' || kind === 'cardPayment') && !hasEligibleCreditCardAccounts) {
        setError('No credit card accounts found. Add one in Dashboard → Credit Cards → Manage Cards.');
        setSnackbarOpen(true);
        return;
      }
      if (kind === 'cardPurchase' && !accountId) {
        setError('Please select a credit card account.');
        setSnackbarOpen(true);
        return;
      }
      if (kind === 'cardPurchase' && !eligibleCreditCardAccounts.some((account) => account._id === accountId)) {
        setError('Selected credit card account no longer exists. Please choose another credit card account.');
        setSnackbarOpen(true);
        return;
      }
      if (kind === 'cardPayment' && (!fromAccountId || !toAccountId)) {
        setError('Please select both a pay from account and a credit card account.');
        setSnackbarOpen(true);
        return;
      }
      if (kind === 'cardPayment' && !selectableAssetAccounts.some((account) => account._id === fromAccountId)) {
        setError('Selected pay from account no longer exists. Please choose another asset account.');
        setSnackbarOpen(true);
        return;
      }
      if (kind === 'cardPayment' && !eligibleCreditCardAccounts.some((account) => account._id === toAccountId)) {
        setError('Selected credit card account no longer exists. Please choose another credit card account.');
        setSnackbarOpen(true);
        return;
      }
      if (!validateLedgerAccounts()) {
        setError('Please select valid account(s) for this kind.');
        setSnackbarOpen(true);
        return;
      }

      let computedNextDueDate = nextDueDate;
      if (isRecurring && !frequency) {
        setError('Recurring transactions require frequency.');
        setSnackbarOpen(true);
        return;
      }
      if (isRecurring && !endDate) {
        setError('Recurring transactions require end date.');
        setSnackbarOpen(true);
        return;
      }
      if (isRecurring && !computedNextDueDate) {
        const calculated = calculateNextDueDate(date, frequency);
        if (!calculated) {
          setError('Unable to calculate next due date for recurring transaction.');
          setSnackbarOpen(true);
          return;
        }
        computedNextDueDate = calculated;
      }

      const payload: Record<string, unknown> = {
        amount: Number(amount),
        kind,
        category,
        description,
        tentative,
        ownerScope,
        visibility,
        date: toUTCNoonDate(date),
        tags,
      };

      if (!disablePaymentEdit) {
        payload.paymentStatus = paymentStatus;
      }

      if (kind === 'income' || kind === 'expense' || kind === 'cardPurchase' || kind === 'adjustment') {
        payload.accountId = accountId;
      }
      if (kind === 'transfer' || kind === 'cardPayment') {
        payload.fromAccountId = fromAccountId;
        payload.toAccountId = toAccountId;
      }
      if (kind === 'adjustment') {
        payload.adjustmentDirection = adjustmentDirection;
        payload.amountSigned = adjustmentDirection === 'decrease' ? -Math.abs(Number(amount)) : Math.abs(Number(amount));
      }
      if (isRecurring && supportsLedgerRecurringKind) {
        payload.isRecurring = true;
        payload.frequency = frequency;
        payload.endDate = toUTCNoonDate(endDate);
        payload.nextDueDate = computedNextDueDate ? toUTCNoonDate(computedNextDueDate) : undefined;
      }

      if (submitOverride) {
        const excludedKeys = new Set<string>();
        if (disableDateEdit) excludedKeys.add('date');
        if (disablePaymentEdit) {
          excludedKeys.add('paymentStatus');
          excludedKeys.add('paidAt');
        }
        const overridePayload = excludedKeys.size > 0
          ? Object.fromEntries(Object.entries(payload).filter(([key]) => !excludedKeys.has(key)))
          : payload;
        try {
          await submitOverride(overridePayload);
          onSuccess();
        } catch (err: unknown) {
          const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
          setError(errorMessage);
          setSnackbarOpen(true);
        }
        return;
      }

      try {
        const res = await authFetch(
          transaction ? `/api/ledger/transactions/${transaction._id}` : '/api/ledger/transactions',
          {
            method: transaction ? 'PUT' : 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.token}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || 'Failed to save transaction');
        }

        if (!transaction) {
          setAmount('');
          setKind('expense');
          setCategory('');
          setDescription('');
          setDate('');
          setTentative(false);
          setOwnerScope('household');
          setVisibility('shared');
          setTags([]);
          setAccountId(defaultCheckingId);
          setFromAccountId(defaultCheckingId);
          setToAccountId(defaultSavingsId);
          setAdjustmentDirection('increase');
          setIsRecurring(false);
          setFrequency('');
          setEndDate('');
          setNextDueDate('');
        }

        onSuccess();
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
        setError(errorMessage);
        setSnackbarOpen(true);
      }

      return;
    }

    setError('Editing legacy transactions is temporarily disabled while this feature is migrated to the new ledger system.');
    setSnackbarOpen(true);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <TextField
          label="Amount"
          type="number"
          fullWidth
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          required
        />

        {isLedgerMode ? (
          <FormControl fullWidth required>
            <InputLabel>Kind</InputLabel>
            <Select
              value={kind}
              label="Kind"
              onChange={(e) => setKind(e.target.value as LedgerKind)}
            >
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="expense">Expense</MenuItem>
              <MenuItem value="transfer">Transfer</MenuItem>
              <MenuItem value="cardPurchase">Card Purchase</MenuItem>
              <MenuItem value="cardPayment">Card Payment</MenuItem>
              <MenuItem value="adjustment">Balance Adjustment</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value as LegacyType)}
            >
              <MenuItem value="income">Income</MenuItem>
              <MenuItem value="expense">Expense</MenuItem>
              <MenuItem value="savings">Savings</MenuItem>
            </Select>
          </FormControl>
        )}

        {isLedgerMode && (kind === 'income' || kind === 'expense') && (
          <FormControl fullWidth required>
            <InputLabel>Account</InputLabel>
            <Select
              value={accountId}
              label="Account"
              onChange={(e) => setAccountId(e.target.value)}
              disabled={accountsLoading}
            >
              {selectableAssetAccounts.map((account) => (
                <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isLedgerMode && kind === 'transfer' && (
          <>
            <FormControl fullWidth required>
              <InputLabel>From Account</InputLabel>
              <Select
                value={fromAccountId}
                label="From Account"
                onChange={(e) => setFromAccountId(e.target.value)}
                disabled={accountsLoading}
              >
                {selectableAssetAccounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>To Account</InputLabel>
              <Select
                value={toAccountId}
                label="To Account"
                onChange={(e) => setToAccountId(e.target.value)}
                disabled={accountsLoading}
              >
                {selectableAssetAccounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {isLedgerMode && kind === 'cardPurchase' && (
          <FormControl fullWidth required>
            <InputLabel>Credit Card</InputLabel>
            <Select
              value={accountId}
              label="Credit Card"
              onChange={(e) => setAccountId(e.target.value)}
              disabled={accountsLoading}
            >
              {eligibleCreditCardAccounts.map((account) => (
                <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isLedgerMode && kind === 'cardPayment' && (
          <>
            <FormControl fullWidth required>
              <InputLabel>Pay From Account</InputLabel>
              <Select
                value={fromAccountId}
                label="Pay From Account"
                onChange={(e) => setFromAccountId(e.target.value)}
                disabled={accountsLoading}
              >
                {selectableAssetAccounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Credit Card</InputLabel>
              <Select
                value={toAccountId}
                label="Credit Card"
                onChange={(e) => setToAccountId(e.target.value)}
                disabled={accountsLoading}
              >
                {eligibleCreditCardAccounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {isLedgerMode && (kind === 'cardPurchase' || kind === 'cardPayment') && !hasEligibleCreditCardAccounts && (
          <Alert severity="warning">
            No credit card accounts found. Add one in Dashboard → Credit Cards → Manage Cards.
          </Alert>
        )}
        {(selectedCardPurchaseAccountMissing || selectedCardPaymentCreditCardMissing) && (
          <Alert severity="warning">
            Selected credit card account no longer exists. Please choose another credit card account.
          </Alert>
        )}
        {showMissingBankAccountsAlert && (
          <Alert
            severity="warning"
            action={(
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setManageCheckingOpen(true)}
                >
                  Create Checking
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setManageSavingsOpen(true)}
                >
                  Create Savings
                </Button>
              </Stack>
            )}
          >
            You need to create a Checking/Savings account before adding transactions.
          </Alert>
        )}

        {isLedgerMode && kind === 'adjustment' && (
          <>
            <FormControl fullWidth required>
              <InputLabel>Account</InputLabel>
              <Select
                value={accountId}
                label="Account"
                onChange={(e) => setAccountId(e.target.value)}
                disabled={accountsLoading}
              >
                {selectableAssetAccounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>{account.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Direction</InputLabel>
              <Select
                value={adjustmentDirection}
                label="Direction"
                onChange={(e) => setAdjustmentDirection(e.target.value as AdjustmentDirection)}
              >
                <MenuItem value="increase">Increase</MenuItem>
                <MenuItem value="decrease">Decrease</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        <TextField
          label="Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={disableDateEdit}
          required
        />
        <FormControl fullWidth disabled={disablePaymentEdit}>
          <InputLabel>Payment Status</InputLabel>
          <Select
            label="Payment Status"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value as 'unpaid' | 'paid')}
            inputProps={{ 'data-testid': 'payment-status-select' }}
          >
            <MenuItem value="unpaid">Unpaid</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
          </Select>
        </FormControl>
        {showRecurringBlock && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isRecurring}
                onChange={(e) => {
                  setIsRecurring(e.target.checked);
                  if (!e.target.checked) {
                    setFrequency('');
                    setNextDueDate('');
                    setEndDate('');
                  }
                }}
              />
            }
            label="Enable Recurring Transaction"
          />
        )}
        {showRecurringBlock && isRecurring && (
          <>
            <FormControl fullWidth required>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={frequency}
                label="Frequency"
                onChange={(e) => setFrequency(e.target.value as typeof frequency)}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Biweekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Next Due Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={nextDueDate}
              onChange={(e) => setNextDueDate(e.target.value)}
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            {isLedgerMode && transaction && (
              <Typography variant="caption" color="text.secondary">
                Edits apply to this instance only.
              </Typography>
            )}
          </>
        )}

        <FormControl fullWidth>
          <InputLabel>Tags</InputLabel>
          <Select
            multiple
            value={tags}
            onChange={(e) => {
              const value = e.target.value;
              setTags(typeof value === 'string' ? value.split(',') : value);
            }}
            label="Tags"
            renderValue={(selected) => selected.join(', ')}
          >
            {tagOptions.map((tag) => (
              <MenuItem key={tag} value={tag}>
                <Checkbox checked={tags.includes(tag)} />
                <Typography>{tag}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Category"
          fullWidth
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          required
        />

        <TextField
          label="Description"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <FormControlLabel
          control={<Checkbox checked={tentative} onChange={(e) => setTentative(e.target.checked)} />}
          label={transaction?.tentative ? '✅ Confirm this transaction by unchecking' : '📝 Mark as Tentative (for planning)'}
        />

        <FormControl fullWidth>
          <InputLabel>Attribution</InputLabel>
          <Select
            value={ownerScope}
            label="Attribution"
            onChange={(e) => setOwnerScope(e.target.value as typeof ownerScope)}
          >
            <MenuItem value="household">Household</MenuItem>
            <MenuItem value="primary">Primary</MenuItem>
            <MenuItem value="partner">Partner</MenuItem>
            <MenuItem value="child">Child</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          Sharing is controlled by account settings (Private vs Household).
        </Typography>



        <Button type="submit" variant="contained" color="primary" disabled={disableSubmit}>
          Save Transaction
        </Button>
      </Stack>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <ManageAssetAccountsModal
        open={manageCheckingOpen}
        onClose={() => setManageCheckingOpen(false)}
        accountType="checking"
        accounts={activeCheckingAccounts}
        token={auth?.token || null}
        onSaved={refetch}
      />
      <ManageAssetAccountsModal
        open={manageSavingsOpen}
        onClose={() => setManageSavingsOpen(false)}
        accountType="savings"
        accounts={activeSavingsAccounts}
        token={auth?.token || null}
        onSaved={refetch}
      />
    </form>
  );
};

export default TransactionForm;
